import { db } from "../firebase/firebase";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

/* ---------------------------------------------------------
   NORMALIZE NAME (consistent everywhere)
--------------------------------------------------------- */
export function normalizeName(name = "") {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/* ---------------------------------------------------------
   OPTION A — SMART NORMALIZATION (Recommended)
   - Removes punctuation
   - Removes useless words
   - Keeps first 3 meaningful words
--------------------------------------------------------- */
export function normalizeKey(name = "") {
  if (!name) return "";

  // 1) Clean all punctuation
  let clean = name
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "") // remove commas, dots, hyphens
    .replace(/\s+/g, " ")
    .trim();

  const parts = clean.split(" ");

  // 2) Ignore useless/frequent words
  const skipWords = ["mr", "mrs", "ms", "driver", "chauffeur", "sir"];
  const filtered = parts.filter((w) => !skipWords.includes(w));

  // 3) Return FIRST 3 meaningful words
  return filtered.slice(0, 3).join(" ");
}

/* ---------------------------------------------------------
   LOAD ALL MAPPINGS FROM manualMappings
   RETURNS:
     { normalizedName: driverId }
--------------------------------------------------------- */
export async function loadManualMappings() {
  const snap = await getDocs(collection(db, "driverPlatformMappings"));
  const allMappings = {};

  snap.docs.forEach((d) => {
    const id = d.id;
    const data = d.data();

    if (id === "manualMappings") {
      Object.keys(data).forEach((k) => {
        if (k !== "updatedAt" && k !== "source") {
          allMappings[k] = data[k]; // normalizedKey → driverId
        }
      });
    }
  });

  return allMappings;
}

/* ---------------------------------------------------------
   SAVE MAPPING INTO manualMappings DOCUMENT
--------------------------------------------------------- */
export async function saveManualMapping(nameNorm, driverId, driverName) {
  await setDoc(
    doc(db, "driverPlatformMappings", "manualMappings"),
    {
      [nameNorm]: driverId,
      updatedAt: serverTimestamp(),
      source: "manual",
    },
    { merge: true }
  );
}

/* ---------------------------------------------------------
   FUZZY MATCH AS A FALLBACK ONLY
--------------------------------------------------------- */
export function fuzzyMatchDriver(manualNameNorm, driverList) {
  const norm = manualNameNorm;

  // Exact match
  let match = driverList.find(
    (d) => normalizeName(d.fullName || "") === norm
  );
  if (match) return match.id;

  // Contains
  match = driverList.find((d) =>
    normalizeName(d.fullName || "").includes(norm)
  );
  if (match) return match.id;

  // Reverse contains
  match = driverList.find((d) =>
    norm.includes(normalizeName(d.fullName || ""))
  );
  if (match) return match.id;

  return null;
}

/* ---------------------------------------------------------
   MAIN DRIVER RESOLVER FOR ALL MODULES
--------------------------------------------------------- */
export async function resolveManualTripDriver(trip, driverList) {
  const rawName = trip.driver || "";
  const nameNorm = normalizeKey(rawName); // Option-A applied

  const mappings = await loadManualMappings();

  // 1) Already saved mapping
  if (mappings[nameNorm]) {
    const driverId = mappings[nameNorm];
    const driverInfo = driverList.find((d) => d.id === driverId);

    return {
      driverId,
      driverName: driverInfo?.fullName || rawName,
      isMapped: true,
      nameNorm,
    };
  }

  // 2) Fuzzy match
  const fuzzyId = fuzzyMatchDriver(nameNorm, driverList);

  if (fuzzyId) {
    const driverInfo = driverList.find((d) => d.id === fuzzyId);

    // Save permanently
    await saveManualMapping(nameNorm, fuzzyId, driverInfo?.fullName);

    return {
      driverId: fuzzyId,
      driverName: driverInfo?.fullName || rawName,
      isMapped: true,
      nameNorm,
    };
  }

  // 3) Unmapped
  return {
    driverId: null,
    driverName: rawName,
    isMapped: false,
    nameNorm,
  };
}
